import React, { useContext } from 'react'
import { ListOfFacility } from '@s4l/stayforlong/shared/ListOfFacility'
import type { Facility } from '@s4l/stayforlong/shared/Facility'
import { useS4LContext } from '@s4l/react-context'
import MappedIconText from './MappedIconText'
import { PropertyCardContext } from '../../Organisms/PropertyCard/context'

// prettier-ignore
const FACILITY_TRANSLATION_MAP: Record<string, string> = {
    // Featured facilities
    'air-conditioner': 'global.air-conditioner',
    'airport-shuttle': 'global.airport-shuttle',
    'parking': 'global.car-park', 
    'car-park': 'global.car-park', 
    'garage': 'global.car-park', 
    'gym': 'global.gym', 
    'receptionist': 'global.24h-reception', 
    '24-hour-reception': 'global.24h-reception',
    'safe': 'global.safebox', 
    'spa': 'global.spa', 
    'pool': 'global.pool', 
    'outdoor': 'global.outdoor-pool', 
    'outdoor-pool': 'global.outdoor-pool', 
    'indoor': 'global.indoor-pool', 
    'indoor-pool': 'global.indoor-pool', 
    'wheelchair': 'global.wheelchair', 
    'wifi-alt': 'global.wifi',
    'wifi': 'global.wifi', 
    'wi-fi': 'global.wifi',
    'freeCancellation':  'global.free-cancellation',
    'accessible': 'facilities.accessible',
    'wheelchair-access': 'global.accessible',
  
    // Common facilities
    'air-conditioning-in-public-areas': 'global.air-conditioner',
    'centrally-regulated-air-conditioning': 'global.air-conditioner',
    'individually-adjustable-air-conditioning': 'global.air-conditioner',
    'breakfast': 'global.breakfast', 
    'hotel-safe': 'global.safebox',
    'restaurant': 'global.restaurant', 
    'terrace': 'global.terrace', 
}

interface FacilitiesProps {
  facilitiesJSON?: ReturnType<ListOfFacility['toJSON']>
  facilities?: ListOfFacility
  hide?: boolean
  maxFacilities?: number
  size?: 's' | 'l' | 'xl'
}

export const Facilities = ({
  facilitiesJSON,
  facilities,
  hide = false,
  maxFacilities,
  size = 'l',
}: FacilitiesProps) => {
  const { i18n } = useS4LContext()
  const { isSoldOut } = useContext(PropertyCardContext)

  if (!facilitiesJSON && !facilities) return null

  if (hide || isSoldOut) {
    return null
  }

  const listofFacilities = facilities ?? ListOfFacility.fromJSONOrEmpty({ json: facilitiesJSON })

  if (listofFacilities.isEmpty()) {
    return null
  }

  let validFacilities = listofFacilities.uniqueById().filter((facility: Facility) => {
    const translationKey = FACILITY_TRANSLATION_MAP[facility.id]
    return !!translationKey
  })

  if (maxFacilities !== undefined) {
    validFacilities = validFacilities.take(maxFacilities)
  }

  if (validFacilities.isEmpty()) {
    return null
  }

  const facilitiesElements = validFacilities.map((facility: Facility) => {
    const translationKey = FACILITY_TRANSLATION_MAP[facility.id]
    const translatedText = i18n.formatMessage({ id: translationKey })

    return (
      <span
        key={facility.id}
        style={{ display: 'inline-block', marginInlineEnd: 'var(--space-8)' }}
      >
        <MappedIconText
          facilityId={facility.id}
          text={translatedText}
          size={size}
          color="var(--color-neutral-600)"
        />
      </span>
    )
  })

  return <>{facilitiesElements}</>
}

Facilities.displayName = 'Facilities'
